import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'
import { ChevronRight } from 'lucide-react'

import { cn } from '@/lib/cn'
import PaymentBadge from '@/features/vacation/payment-badge'
import type {
  VacationRequest,
  VacationStatus,
  VacationType,
} from '@/features/vacation/use-vacation'

const TYPE_LABEL: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const DECISION_LABEL: Record<VacationStatus, string> = {
  PENDING: 'Oczekuje na decyzję',
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
  CANCELLED: 'Anulowany',
}

/** `createdAt` / `decidedAt` to pełne DateTime — pokazujemy datę z godziną. */
export function formatSubmittedAt(iso: string): string {
  return format(parseISO(iso), "d MMM yyyy, HH:mm", { locale: pl })
}

/** Krótka forma do kolumny „Wpłynął" — pełna data siedzi w rozwinięciu. */
export function formatSubmittedShort(iso: string): string {
  return format(parseISO(iso), 'd MMM', { locale: pl })
}

/** „5 dni temu" — do zdania samodzielnego. */
export function formatWaitingFor(iso: string): string {
  return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: pl })
}

/** „5 dni" — bez sufiksu, do złożenia z własnym słowem („czeka 5 dni"). */
export function formatWaitingDuration(iso: string): string {
  return formatDistanceToNow(parseISO(iso), { locale: pl })
}

function dayOnly(value: string): string {
  const parts = value.split('-').map(Number)
  return format(new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!)), 'd MMMM yyyy', {
    locale: pl,
  })
}

/**
 * Rozwijane szczegóły wniosku — wspólne dla listy pracownika i panelu admina.
 * Zastępuje tooltipy `title`, które na telefonie (brak hovera) były niedostępne.
 */
export default function VacationRequestDetails({
  request,
}: {
  request: VacationRequest
}) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
      <Field label="Wpłynął">
        <span className="tabular-nums">{formatSubmittedAt(request.createdAt)}</span>
        <span className="text-text-faint block text-xs mt-0.5">
          {formatWaitingFor(request.createdAt)}
        </span>
      </Field>

      <Field label="Typ">
        <span className="inline-flex items-center gap-1.5">
          {TYPE_LABEL[request.type]}
          <PaymentBadge type={request.type} />
        </span>
      </Field>

      <Field label="Od">{dayOnly(request.startDate)}</Field>
      <Field label="Do">{dayOnly(request.endDate)}</Field>

      <Field label="Dni robocze">
        <span className="tabular-nums font-medium">{request.workingDays}</span>
      </Field>

      <Field label="Decyzja">
        {DECISION_LABEL[request.status]}
        {request.decidedBy && (
          <span className="text-text-muted"> — {request.decidedBy.name}</span>
        )}
        {request.decidedAt && (
          <span className="text-text-faint block text-xs mt-0.5 tabular-nums">
            {formatSubmittedAt(request.decidedAt)}
          </span>
        )}
      </Field>

      {request.comment && (
        <Field label="Komentarz pracownika" wide>
          <span className="italic">„{request.comment}"</span>
        </Field>
      )}

      {request.decisionNote && (
        <Field label="Uzasadnienie decyzji" wide>
          <span className="italic">„{request.decisionNote}"</span>
        </Field>
      )}
    </dl>
  )
}

/**
 * Chevron rozwijający wiersz. Cały wiersz też reaguje na klik (myszą), ale to
 * ten przycisk niesie stan dla czytników ekranu i obsługę klawiatury.
 */
export function ExpandToggle({
  expanded,
  panelId,
  onToggle,
}: {
  expanded: boolean
  panelId: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={panelId}
      aria-label={expanded ? 'Zwiń szczegóły wniosku' : 'Pokaż szczegóły wniosku'}
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-action text-text-faint hover:text-text-primary hover:bg-bg-surface transition focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
    >
      <ChevronRight
        className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')}
        aria-hidden
      />
    </button>
  )
}

function Field({
  label,
  children,
  wide,
}: {
  label: string
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-[11px] uppercase tracking-wider text-text-faint mb-0.5">
        {label}
      </dt>
      <dd className="text-text-primary">{children}</dd>
    </div>
  )
}
